import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { useUrlFilters } from "@/lib/urlfilters";
import { fmtDT, peso } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  // The dashboard has two invoice tiles meaning different things:
  // ?status=outstanding (approved/delivered/partial) and ?status=draft.
  const [f, setF] = useUrlFilters({ status: "all" });
  const [contracts, setContracts] = useState([]);
  const [sel, setSel] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({ contract_id: "", from_date: "", to_date: "" });
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", reference: "" });

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); api.get("/contracts").then((r) => setContracts(r.data)).catch(() => {}); }, []);

  const pick = async (inv) => {
    const { data } = await api.get(`/invoices/${inv.id}`);
    setSel(data);
  };

  const generate = async () => {
    try {
      const { data } = await api.post("/invoices/generate", genForm);
      toast.success(`Invoice ${data.ref_no} generated — ${peso(data.total)}`);
      setGenOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const transition = async (to) => {
    try {
      const { data } = await api.post(`/invoices/${sel.id}/transition`, { to });
      toast.success(`Invoice → ${to}`);
      setSel(data);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const recordPayment = async () => {
    try {
      const { data } = await api.post(`/invoices/${sel.id}/payment`, { amount: parseFloat(payForm.amount) || 0, reference: payForm.reference });
      toast.success(`Payment recorded — status ${data.status}`);
      setPayOpen(false);
      setSel(data);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const paid = (inv) => (inv.payments || []).reduce((s, p) => s + p.amount, 0);

  const OUTSTANDING = ["approved", "delivered", "partially_paid"];
  const shownInvoices = f.status === "all" ? invoices
    : f.status === "outstanding" ? invoices.filter((r) => OUTSTANDING.includes(r.status))
      : invoices.filter((r) => r.status === f.status);

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Billing & Invoicing" sub="Billable lines from approved attendance + fixed fees · draft → approve → deliver → payment"
        actions={<Button data-testid="generate-invoice-button" className="rounded-none" onClick={() => setGenOpen(true)}>Generate invoice</Button>} />
      <div className="flex flex-wrap gap-2 mb-3 items-end" data-testid="invoice-filters">
        <div className="w-72">
          <Label className="text-[10px] uppercase font-bold">Status</Label>
          <Select value={f.status} onValueChange={(v) => setF({ status: v })}>
            <SelectTrigger data-testid="invoice-status-filter" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="outstanding">Outstanding (approved / delivered / partial)</SelectItem>
              <SelectItem value="draft">Draft — not yet sent</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="partially_paid">Partially paid</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="void">Void</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DataTable testid="invoices-table" onRowClick={pick} rows={shownInvoices} empty="No invoices yet"
        columns={[
          { key: "ref_no", label: "Ref", render: (r) => <span className="font-mono text-xs">{r.ref_no}</span> },
          { key: "client_name", label: "Client", render: (r) => <div><div className="font-semibold">{r.client_name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.contract_ref}</div></div> },
          { key: "period", label: "Period", render: (r) => <span className="font-mono text-xs">{r.from_date} → {r.to_date}</span> },
          { key: "total", label: "Total", render: (r) => <span className="font-mono text-xs font-bold">{peso(r.total)}</span> },
          { key: "paid", label: "Paid", render: (r) => <span className="font-mono text-xs">{peso(paid(r))}</span> },
          { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
          { key: "created_at", label: "Created", render: (r) => <span className="font-mono text-xs">{fmtDT(r.created_at)}</span> },
        ]} />

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="rounded-none" data-testid="generate-dialog">
          <DialogHeader><DialogTitle className="font-heading">Generate invoice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Contract</Label>
              <Select value={genForm.contract_id} onValueChange={(v) => setGenForm({ ...genForm, contract_id: v })}>
                <SelectTrigger data-testid="gen-contract" className="rounded-none mt-1"><SelectValue placeholder="Select contract" /></SelectTrigger>
                <SelectContent>{contracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.ref_no} — {c.client_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider font-bold">From</Label><Input data-testid="gen-from" type="date" className="rounded-none mt-1" value={genForm.from_date} onChange={(e) => setGenForm({ ...genForm, from_date: e.target.value })} /></div>
              <div><Label className="text-xs uppercase tracking-wider font-bold">To</Label><Input data-testid="gen-to" type="date" className="rounded-none mt-1" value={genForm.to_date} onChange={(e) => setGenForm({ ...genForm, to_date: e.target.value })} /></div>
            </div>
            <Button data-testid="gen-submit" className="w-full rounded-none" onClick={generate} disabled={!genForm.contract_id || !genForm.from_date || !genForm.to_date}>Generate from approved attendance</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sel} onOpenChange={() => setSel(null)}>
        <DialogContent className="rounded-none max-w-3xl max-h-[85vh] overflow-auto" data-testid="invoice-detail-dialog">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">{sel.ref_no} <StatusBadge value={sel.status} /></DialogTitle>
              </DialogHeader>
              <div className="font-mono text-[10px] text-muted-foreground mb-3">{sel.client_name} · {sel.contract_ref} · {sel.from_date} → {sel.to_date}</div>
              <DataTable testid="invoice-lines-table" rows={sel.lines.map((l, i) => ({ ...l, id: i }))}
                columns={[
                  { key: "description", label: "Line", render: (r) => <span className="text-xs">{r.description}</span> },
                  { key: "kind", label: "Kind", render: (r) => <StatusBadge value={r.kind === "fixed_fee" ? "delivered" : "published"} /> },
                  { key: "qty", label: "Qty", render: (r) => <span className="font-mono text-xs">{r.qty}</span> },
                  { key: "unit_rate", label: "Rate", render: (r) => <span className="font-mono text-xs">{peso(r.unit_rate)}</span> },
                  { key: "amount", label: "Amount", render: (r) => <span className="font-mono text-xs font-bold">{peso(r.amount)}</span> },
                ]} />
              <div className="text-right mt-3 space-y-1">
                <div className="font-mono text-xs">Subtotal {peso(sel.subtotal)}</div>
                <div className="font-mono text-xs">VAT {sel.vat_percent}% — {peso(sel.vat)}</div>
                <div className="font-mono text-lg font-bold" data-testid="invoice-total">Total {peso(sel.total)}</div>
                <div className="font-mono text-xs text-muted-foreground">Paid {peso(paid(sel))}</div>
              </div>
              {(sel.payments || []).length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Payments</div>
                  {sel.payments.map((p, i) => <div key={i} className="font-mono text-xs border-t border-border py-1">{peso(p.amount)} · {p.reference || "no ref"} · {p.by} · {fmtDT(p.at)}</div>)}
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                {sel.status === "draft" && <Button size="sm" className="rounded-none" data-testid="invoice-approve" onClick={() => transition("approved")}>Approve</Button>}
                {sel.status === "approved" && <Button size="sm" className="rounded-none" data-testid="invoice-deliver" onClick={() => transition("delivered")}>Mark delivered</Button>}
                {["approved", "delivered", "partially_paid"].includes(sel.status) && (
                  <Button size="sm" variant="outline" className="rounded-none" data-testid="invoice-payment" onClick={() => { setPayForm({ amount: String(sel.total - paid(sel)), reference: "" }); setPayOpen(true); }}>Record payment</Button>
                )}
                <a href={`${API}/invoices/${sel.id}/export?format=csv`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-none" data-testid="invoice-export-csv">CSV</Button></a>
                <a href={`${API}/invoices/${sel.id}/export?format=xlsx`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-none" data-testid="invoice-export-xlsx">XLSX</Button></a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-none" data-testid="payment-dialog">
          <DialogHeader><DialogTitle className="font-heading">Record payment — {sel?.ref_no}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider font-bold">Amount (₱)</Label><Input data-testid="payment-amount" type="number" className="rounded-none mt-1" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Reference (OR/check no.)</Label><Input data-testid="payment-reference" className="rounded-none mt-1" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} /></div>
            <Button data-testid="payment-submit" className="w-full rounded-none" onClick={recordPayment} disabled={!payForm.amount}>Record</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
